/**
 * Lock-reservations schemas (offer-accept-reservation).
 *
 * `POST /offers/:offerId/versions/:versionId/lock-reservations` request/response
 * shapes. Resolves every unit+window pair in an ACCEPTED version's snapshot to
 * exactly one of three outcomes: locked, queued, failed.
 *
 * Traceability: offer-accept-reservation / Scenario: Mixed outcome across three
 * unit+window pairs.
 */
import { z } from 'zod';
import {
  BatchReserveCellSchema,
  BatchReserveConflictCellSchema,
  BatchReserveQueuedCellSchema,
  BatchReserveRequestBaseSchema,
  BatchReserveResultCellSchema,
  MinWindowDaysSchema,
  SelectedDaysSchema,
  TtlExpiresAtSchema,
} from './reservation-batch.js';

// Reuses BatchReserveRequestBaseSchema's exact `status` enum — no new status
// value invented (see design.md D-1..D-5, proposal.md "Affected Boundaries").
// Uses the base (unrefined) schema because BatchReserveRequestSchema is a
// ZodEffects (superRefine) and has no `.shape`.
export const LockReservationsRequestSchema = z.object({
  status: BatchReserveRequestBaseSchema.shape.status,
  /**
   * REQUIRED shared "do kiedy" deadline (CU-86cbc4t4q) — the SAME field name
   * batch-reserve already ships, under the SAME wire shape (see
   * TtlExpiresAtSchema). One deadline for the entire client-accepted package,
   * applied to every locked pair and carried onto every queued pair's
   * target expiry once promoted.
   */
  ttlExpiresAt: TtlExpiresAtSchema,
});
export type LockReservationsRequest = z.infer<typeof LockReservationsRequestSchema>;

export const LockReservationsResponseSchema = z.object({
  locked: z.array(BatchReserveResultCellSchema),
  // Reuses BatchReserveQueuedCellSchema (reservation-batch.ts) directly — the
  // two schemas were structurally identical, independently declared
  // duplicates (action-breakdown-remaining-dialogs / "Duplicate outcome cell
  // declarations"), collapsed here into the one batch-reserve already owns.
  queued: z.array(BatchReserveQueuedCellSchema),
  failed: z.array(BatchReserveConflictCellSchema),
});
export type LockReservationsResponse = z.infer<typeof LockReservationsResponseSchema>;

// ─── Lock preview (offer-accept-reservation / "Lock-reservations preview") ──
//
// Read-only preview of what locking a version's accepted nośniki would do.
// Takes the intended hold type as a query param (GET), because the outcome
// differs by type: a hold the queue accepts is queued, an option never is.

export const LockPreviewRequestSchema = z
  .object({
    status: BatchReserveRequestBaseSchema.shape.status,
  })
  .strict();
export type LockPreviewRequest = z.infer<typeof LockPreviewRequestSchema>;

export const LockPreviewItemSchema = z.discriminatedUnion('outcome', [
  BatchReserveCellSchema.extend({ outcome: z.literal('lockable') }),
  BatchReserveCellSchema.extend({ outcome: z.literal('already_held') }),
  // Fixup (action-breakdown-remaining-dialogs task 2.1 review finding):
  // deliberately NOT BatchReserveQueuedCellSchema — that schema's
  // `queueEntryId` is a real, persisted queue-entry id, which only exists
  // once a join has actually happened. A read-only preview reports "would be
  // queued" for a nośnik that has NEVER been queued before (offer-accept-
  // reservation spec's first scenario) and so has no such id to report
  // without writing one, which the preview is forbidden from doing (spec.md:11
  // "SHALL NOT ... take the exclusive holds the lock action takes"). Matches
  // the actual shared `action-breakdown` "queued" vocabulary instead —
  // `CampaignOutcomeItemSchema`'s own 'queued' member (campaign-sale.ts),
  // which carries only `position`, no `queueEntryId`.
  BatchReserveCellSchema.extend({
    outcome: z.literal('queued'),
    position: z.number().int().positive(),
  }),
  BatchReserveCellSchema.extend({
    outcome: z.literal('unresolvable'),
    reason: z.string(),
    // Required-vs-selected day counts for a minimum-reservation-window
    // breach — reused from the shared declaration (action-breakdown / "One
    // outcome vocabulary across fan-out actions") rather than restated here.
    minWindowDays: MinWindowDaysSchema,
    selectedDays: SelectedDaysSchema,
  }),
]);
export type LockPreviewItem = z.infer<typeof LockPreviewItemSchema>;

export const LockPreviewResponseSchema = z.array(LockPreviewItemSchema);
export type LockPreviewResponse = z.infer<typeof LockPreviewResponseSchema>;
